import clsx from 'clsx';
import { useId, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

import { Tag } from './tag';
import { Typography } from './typography';

// ---------------------------------------------------------------------------
// TagInput — multi-value free-text input. The user types a value and commits
// it as a removable chip with Enter (or comma). Backspace on an empty input
// removes the last tag. Used for VONQ TEXTEXPAND posting requirement fields,
// whose value is submitted as an array of strings.
// ---------------------------------------------------------------------------

type TagInputProps = {
  value: string[];
  onChange: (value: string[]) => void;
  id?: string;
  placeholder?: string;
  /** Max characters per tag (from the field's `maxlength` rule). */
  maxLength?: number;
  error?: string;
  disabled?: boolean;
};

export function TagInput({
  value,
  onChange,
  id,
  placeholder,
  maxLength,
  error,
  disabled,
}: TagInputProps) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const tag = draft.trim();
    if (!tag) return;
    // Dedupe — VONQ rejects repeated values and they add no signal.
    if (!value.includes(tag)) onChange([...value, tag]);
    setDraft('');
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      removeAt(value.length - 1);
    }
  };

  return (
    <div
      className={clsx(
        'border-fx-neutral-300 flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-sm border bg-white px-2 py-1.5',
        'focus-within:border-fx-brand-600 focus-within:shadow-selected',
        error && '!border-fx-rose',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      {value.map((tag, index) => (
        <Tag
          key={tag}
          text={tag}
          className="bg-fx-neutral-100"
          onClose={() => removeAt(index)}
        />
      ))}
      <input
        id={id}
        value={draft}
        disabled={disabled}
        maxLength={maxLength}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        // Commit a pending draft on blur so a typed-but-not-entered value
        // isn't silently lost when the user clicks away.
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder : undefined}
        className="placeholder:text-fx-neutral-600 h-6 min-w-[6rem] flex-1 bg-transparent text-sm outline-none"
      />
    </div>
  );
}

type FormTagInputProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: string;
  required?: boolean;
  error?: string;
  /** Optional hint shown below the input (e.g. "Hit enter to add your tag(s)"). */
  helperText?: string;
} & Omit<TagInputProps, 'value' | 'onChange'>;

export function FormTagInput<TFieldValues extends FieldValues>({
  name,
  control,
  label,
  required,
  error,
  helperText,
  ...props
}: FormTagInputProps<TFieldValues>) {
  const id = useId();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        // Coerce legacy/undefined values into an array so the control is
        // always in multi-value mode.
        const value = Array.isArray(field.value)
          ? (field.value as string[])
          : field.value
            ? [String(field.value)]
            : [];

        return (
          <div className="flex w-full flex-col gap-1">
            {label && (
              <label htmlFor={id}>
                <Typography variant="subtitle2">
                  {label} {required && <span className="text-fx-rose">*</span>}
                </Typography>
              </label>
            )}
            <TagInput
              id={id}
              value={value}
              onChange={field.onChange}
              error={error}
              {...props}
            />
            {error ? (
              <Typography variant="small1" className="!text-fx-rose">
                {error}
              </Typography>
            ) : (
              helperText && (
                <Typography variant="small1" className="!text-fx-neutral-600">
                  {helperText}
                </Typography>
              )
            )}
          </div>
        );
      }}
    />
  );
}
